import SquaresFourBoldIcon from '@duyank/icons/bold/SquaresFourBold';
import PlusIcon from '@duyank/icons/regular/Plus';
import {
  getTransformStyle,
  isElementInViewport,
  isPointInsideBox,
  LayerComponentProps,
  rectangleInsideAnother,
  SerializedPage,
  visualCorners,
} from '@lidojs/design-core';
import { EditorTemplate } from '../types';
import { GlobalStyle } from '@lidojs/design-layers';
import { getPosition, isMouseEvent, isTouchEvent } from '@lidojs/design-utils';
import React, {
  FC,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { isMobile } from 'react-device-detect';
import { useEditor, useSelectedLayers } from '../hooks';
import useClickOutside from '../hooks/useClickOutside';
import { useDragLayer } from '../hooks/useDragLayer';
import { useSelectLayer } from '../hooks/useSelectLayer';
import useShortcut from '../hooks/useShortcut';
import { useTrackingShiftKey } from '../hooks/useTrackingShiftKey';
import { useZoomPage } from '../hooks/useZoomPage';
import LayerContextMenu from '../layers/core/context-menu/LayerContextMenu';
import SelectionBox from '../layers/core/SelectionBox';
import { useUsedFont } from '../layers/hooks/useUsedFont';
import PageSettings from '../settings/PageSettings';
import DesignPage from './DesignPage';
import { DraggingLayer } from './dragging/DraggingLayer';
import { EditorContext } from './EditorContext';
import { useLocation } from 'react-router-dom';
import { Buffer } from 'buffer';
import axios from 'axios';
interface DesignFrameProps {
  data: SerializedPage[];
}

type Template = EditorTemplate & {
  elements: SerializedPage;
};

const DesignFrame: FC<DesignFrameProps> = ({ data }) => {
  const shiftKeyRef = useTrackingShiftKey();
  const frameRef = useRef<HTMLDivElement>(null);
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement[]>([]);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const draggingItemRef = useRef<HTMLDivElement>(null);
  const { usedFonts } = useUsedFont();
  const location = useLocation();
  const {
    config: { assetPath },
  } = useContext(EditorContext);
  const [showPageSettings, setShowPageSettings] = useState(false);
  useShortcut(frameRef);
  const {
    actions,
    scale,
    pages,
    hoveredPage,
    hoveredLayer,
    selectStatus,
    rotateData,
    resizeData,
    controlBox,
    activePage,
    dragData,
    imageEditor,
    pageSize,
    dragNDrop,
  } = useEditor((state, query) => {
    const hoveredPage = parseInt(Object.keys(state.hoveredLayer)[0]);
    const hoverLayerId = state.hoveredLayer[hoveredPage];
    return {
      scale: state.scale,
      pages: state.pages,
      hoveredPage,
      hoveredLayer: hoverLayerId
        ? state.pages[hoveredPage].layers[hoverLayerId]
        : null,
      selectStatus: state.selectData.status,
      rotateData: state.rotateData,
      resizeData: state.resizeData,
      controlBox: state.controlBox,
      activePage: state.activePage,
      dragData: state.dragData,
      imageEditor: state.imageEditor,
      pageSize: query.getPageSize(),
      dragNDrop: state.dragNDrop,
    };
  });
  const {
    pageTransform,
    onZoomStart,
    onZoomMove,
    onZoomEnd,
    onMoveStart,
    onMove,
    onMoveEnd,
    onMovePage,
    onMovePageEnd,
  } = useZoomPage(frameRef, pageRef, pageContainerRef);
  useEffect(() => {
    actions.setData(data);
  }, [data, actions]);

  useClickOutside(
    contextMenuRef,
    () => {
      actions.hideContextMenu();
    },
    'mousedown',
    { capture: true }
  );

  useEffect(() => {
    const drop = () => {
      actions.endDragNDrop();
    };
    window.addEventListener('dragend', drop);
    return () => {
      window.removeEventListener('dragend', drop);
    };
  });
  const boxRef = useRef<HTMLDivElement>(null);
  const { selectedLayerIds } = useSelectedLayers();

  const handleScroll = () => {
    if (!dragData.status && !selectStatus) {
      const viewport = frameRef.current as HTMLDivElement;
      //change active page
      if (
        pageRef.current[activePage] &&
        !isElementInViewport(viewport, pageRef.current[activePage])
      ) {
        pageRef.current.some((page, pageIndex) => {
          if (isElementInViewport(viewport, page)) {
            actions.selectLayers(pageIndex, 'ROOT');
            return true;
          }
          return false;
        });
      }
    }
  };

  const { tmpSelected, onSelectStart } = useSelectLayer({
    frameRef: frameRef,
    pageListRef: pageRef,
    selectionBoxRef: boxRef,
  });

  const { onDragStart } = useDragLayer({
    frameRef,
    pageListRef: pageRef,
  });

  useEffect(() => {
    const updatePosition = (e: DragEvent) => {
      if (draggingItemRef.current) {
        draggingItemRef.current.style.top = `${
          e.clientY -
          ((
            dragNDrop.data.layers[dragNDrop.data.rootId]
              .props as unknown as LayerComponentProps
          ).boxSize.height *
            scale) /
            2
        }px`;
        draggingItemRef.current.style.left = `${
          e.clientX -
          ((
            dragNDrop.data.layers[dragNDrop.data.rootId]
              .props as unknown as LayerComponentProps
          ).boxSize.width *
            scale) /
            2
        }px`;
      }
    };
    window.addEventListener('dragover', updatePosition);
    return () => {
      window.removeEventListener('dragover', updatePosition);
    };
  }, [actions, dragNDrop?.data?.layers, dragNDrop?.data?.rootId, scale]);

  const handMouseDown = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (
        (isMouseEvent(e) && e.button === 2) ||
        imageEditor ||
        (isTouchEvent(e) && e.touches.length > 1)
      ) {
        return;
      }
      const isClickPage = pageRef.current.find((page) =>
        page.contains(e.target as Node)
      );
      const isClickOutPage = pageRef.current.find((page) =>
        (e.target as Node).contains(page)
      );
      const { clientX, clientY } = getPosition(e);
      if (!isClickPage && !isClickOutPage) {
        return;
      }

      let isInsideControlBox = false;
      if (controlBox) {
        const matrix = new WebKitCSSMatrix(
          getTransformStyle({
            rotate: controlBox.rotate,
          })
        );
        const rect = pageRef.current[activePage].getBoundingClientRect();
        const controlBoxCorner = visualCorners(
          {
            width: controlBox.boxSize.width * scale,
            height: controlBox.boxSize.height * scale,
          },
          matrix,
          {
            x: rect.x + controlBox.position.x * scale,
            y: rect.y + controlBox.position.y * scale,
          }
        );
        isInsideControlBox = isPointInsideBox(
          { x: clientX, y: clientY },
          controlBoxCorner
        );
      }
      if (
        hoveredLayer &&
        hoveredLayer.id !== 'ROOT' &&
        !selectedLayerIds.includes(hoveredLayer.id)
      ) {
        if (
          !isInsideControlBox ||
          (controlBox &&
            rectangleInsideAnother(hoveredLayer.data.props, controlBox))
        ) {
          actions.selectLayers(
            hoveredPage,
            hoveredLayer.id,
            shiftKeyRef.current ? 'add' : 'replace'
          );
        }
      }
      if (
        (hoveredLayer &&
          hoveredLayer.id !== 'ROOT' &&
          !hoveredLayer.data.locked) ||
        isInsideControlBox
      ) {
        onDragStart(e);
        e.stopPropagation();
      } else if (isMouseEvent(e)) {
        onSelectStart(e);
        e.stopPropagation();
      } else if (isTouchEvent(e)) {
        onMoveStart(e);
      }
    },
    [hoveredLayer, controlBox, selectedLayerIds, dragData]
  );

  const cursorCSS = () => {
    if (rotateData.status) {
      const cursor = Math.round((rotateData.rotate || 0) / 10);
      return {
        cursor: `url('${assetPath}/cursors/rotate/${
          cursor === 36 ? 0 : cursor
        }.png') 12 12, auto;`,
      };
    } else if (resizeData.status) {
      const rd = {
        bottomLeft: 45,
        left: 90,
        topLeft: 135,
        top: 180,
        topRight: 225,
        right: 270,
        bottomRight: 315,
        bottom: 0,
      };
      const rotate =
        (resizeData.rotate || 0) + rd[resizeData.direction || 'bottom'] + 90;
      const file = Math.round((rotate % 180) / 10);
      return {
        cursor: `url('${assetPath}/cursors/resize/${file}.png') 12 12, auto`,
      };
    } else if (dragData.status) {
      return {
        cursor: 'move',
      };
    }
    return {};
  };

  const getTemplateByUuid = async (uuid: string) => {
    const { data } = await axios.get(`/templates/${uuid}`);
    const buffer = Buffer.from(data.elements, 'base64');
    const decodedString = buffer.toString('utf-8');
    const elements = JSON.parse(decodedString);

    return {
      elements,
    };
  };

  const setTemplate = async (uuid: string) => {
    const template = await getTemplateByUuid(uuid);

    const { elements } = template;
    actions.setPage(activePage, elements);
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const width = parseFloat(queryParams.get('width'));
    const height = parseFloat(queryParams.get('height'));
    const uuid = queryParams.get('uuid');

    if (width && height) {
      actions.changePageSize({ width, height });
    }

    if (uuid) {
      setTemplate(uuid);
    }
  }, [location]);

  return (
    <Fragment>
      <div
        ref={frameRef}
        css={{
          display: 'flex',
          position: 'relative',
          height: '100%',
          overflow: 'scroll',
          ...cursorCSS(),
          '@media (max-width: 1024px)': {
            overflow: 'hidden',
            height: 'calc(100% - 72px)',
          },
        }}
        tabIndex={0}
        onScroll={() => {
          handleScroll();
        }}
        onTouchEnd={onZoomEnd}
        onTouchMove={onZoomMove}
        onTouchStart={onZoomStart}
      >
        <div
          css={(theme) => ({
            position: 'absolute',
            display: 'flex',
            minWidth: '100%',
            minHeight: '100%',
          })}
          onMouseDown={(e) => handMouseDown(e.nativeEvent)}
          onTouchStart={(e) => handMouseDown(e.nativeEvent)}
        >
          <div
            css={(theme) => ({
              position: 'relative',
              display: 'flex',
              flexGrow: 1,
              touchAction: 'pinch-zoom',
            })}
          >
            <div
              ref={pageContainerRef}
              css={{
                display: 'flex',
                position: 'relative',
                flexDirection: 'row',
                justifyContent: 'center',
                margin: 'auto',
                '@media (max-width: 1024px)': {
                  transition: 'transform 250ms linear 0s',
                  margin: 'initial',
                },
              }}
              style={{
                transform: `translateX(-${
                  isMobile ? window.innerWidth * activePage : 0
                }px)`,
              }}
            >
              <div
                css={{
                  marginLeft: 56,
                  '@media (max-width: 1024px)': {
                    display: 'flex',
                    marginLeft: 0,
                  },
                }}
                onTouchEnd={() => {
                  onMoveEnd();
                  onMovePageEnd();
                }}
                onTouchMove={(e) => {
                  onMove(e);
                  onMovePage(e);
                }}
              >
                <GlobalStyle fonts={usedFonts} mode={'editor'} />
                {pages.map((page, index) => (
                  <div
                    key={index}
                    css={{
                      '@media (max-width: 1024px)': {
                        padding: '0 16px',
                        width: window.innerWidth,
                        height: window.innerHeight,
                        overflow: 'hidden',
                      },
                    }}
                  >
                    <DesignPage
                      ref={(el) => el && (pageRef.current[index] = el)}
                      height={pageSize.height}
                      pageIndex={index}
                      transform={pageTransform}
                      width={pageSize.width}
                    />
                  </div>
                ))}
              </div>
              <div
                css={{
                  width: 56,
                  pointerEvents: 'none',
                  '@media (max-width: 1024px)': {
                    width: 0,
                  },
                }}
              />
            </div>
            <LayerContextMenu ref={contextMenuRef} />
            {selectStatus && (
              <SelectionBox
                ref={boxRef}
                selectedLayers={tmpSelected?.selectedLayers}
              />
            )}
          </div>
        </div>
      </div>
      {resizeData.status && (
        <div
          css={{
            position: 'fixed',
            top: `${(resizeData.cursor?.clientY || 0) + 36}px`,
            left: `${(resizeData.cursor?.clientX || 0) + 60}px`,
            whiteSpace: 'nowrap',
            background: '#3a3a4c',
            padding: '3px 8px',
            borderRadius: 4,
            textAlign: 'center',
            color: 'white',
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          w: {Math.round(resizeData.boxSize?.width || 0)} h:{' '}
          {Math.round(resizeData.boxSize?.height || 0)}
        </div>
      )}
      {showPageSettings && (
        <PageSettings onClose={() => setShowPageSettings(false)} />
      )}
      {dragNDrop.status && (
        <div
          ref={draggingItemRef}
          css={(theme) => ({
            position: 'fixed',
            zIndex: 2048,
            top:
              dragNDrop.position.y -
              ((
                dragNDrop.data.layers[dragNDrop.data.rootId]
                  .props as unknown as LayerComponentProps
              ).boxSize.height *
                scale) /
                2,
            left:
              dragNDrop.position.x -
              ((
                dragNDrop.data.layers[dragNDrop.data.rootId]
                  .props as unknown as LayerComponentProps
              ).boxSize.width *
                scale) /
                2,
            pointerEvents: 'none',
          })}
        >
          <DraggingLayer
            layers={dragNDrop.data.layers}
            parentId={dragNDrop.data.rootId}
          />
        </div>
      )}
    </Fragment>
  );
};

export default DesignFrame;
